import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Download, AlertTriangle, CheckCircle, XCircle, TrendingUp, FileText, Loader2, ArrowRight, Mail, User as UserIcon, LockKeyhole, Briefcase, MapPin, Building2, Sparkles, Zap, Share2, GraduationCap, Trophy } from "lucide-react";
import axios from "axios";
import { ResumeDocPane, AtsReadCard } from "@/components/AtsXrayPanel";
import { getMetaAttributionPayload } from "@/utils/metaPixel";
import { useMarketConfig, usePricingForMarket, price as fmtPrice } from "@/utils/marketConfig";
import POSLoader from "@/components/POSLoader";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function safeExternalUrl(value) {
  try {
    const parsed = new URL(String(value || ""));
    const isHttp = parsed.protocol === "https:" || parsed.protocol === "http:";
    return isHttp && parsed.hostname && !parsed.username && !parsed.password ? parsed.href : "";
  } catch {
    return "";
  }
}

function ScoreGauge({ score, category, masked = false }) {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  // Masked mode (score-reveal gate): a full neutral ring and "?" — the real
  // number/category never render, so there is nothing to squint at or inspect.
  const offset = masked ? 0 : circumference - (score / 100) * circumference;
  const color = masked ? "#5B3DF5" : score <= 40 ? "#ef4444" : score <= 70 ? "#eab308" : "#16a34a";

  return (
    <div className="relative w-48 h-48 mx-auto" data-testid="score-gauge">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#E7E7E2" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-[1500ms] ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="ap-display text-5xl font-extrabold text-[var(--ap-ink)]" data-testid="score-value">{masked ? "?" : score}</span>
        <span className="text-xs text-[var(--ap-muted)] uppercase tracking-wider mt-0.5">/100</span>
        <Badge className={`mt-2 text-xs border-0 ${masked ? 'bg-[var(--ap-violet-soft)] text-[var(--ap-violet)]' : score <= 40 ? 'bg-red-500/15 text-red-500' : score <= 70 ? 'bg-yellow-500/15 text-yellow-600' : 'bg-green-500/15 text-green-600'}`} data-testid="score-badge">
          {masked ? "Ready" : category}
        </Badge>
      </div>
    </div>
  );
}

// Inline auth card — used for the score-reveal gate (anonymous users) and the
// payment gate. Copy + default mode adapt per context.
function AuthGate({ onSuccess, title, subtitle, defaultMode = "login" }) {
  const [isLogin, setIsLogin] = useState(defaultMode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      toast.success(isLogin ? "Signed in!" : "Account created!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="ap-card ap-pop relative overflow-hidden border-0" data-testid="auth-gate-card">
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
      <CardHeader className="text-center pt-8">
        <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3 shadow-[0_10px_24px_-12px_rgba(91,61,245,.5)]" style={{ background: "var(--ap-violet-soft)" }}>
          <LockKeyhole className="w-7 h-7 text-[var(--ap-violet)]" />
        </div>
        <CardTitle className="ap-display text-[var(--ap-ink)] text-xl md:text-2xl font-extrabold">
          {title || (isLogin ? "Sign in to Unlock" : "Create Account to Unlock")}
        </CardTitle>
        <CardDescription className="text-[var(--ap-muted)]">
          {subtitle || "Quick login to proceed with payment"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3 max-w-sm mx-auto">
          {!isLogin && (
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
              <Input placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} required={!isLogin}
                className="pl-10 bg-[var(--ap-canvas)] border-[var(--ap-line)] text-[var(--ap-ink)] placeholder:text-gray-400 h-11 rounded-xl" data-testid="gate-name-input" />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="pl-10 bg-[var(--ap-canvas)] border-[var(--ap-line)] text-[var(--ap-ink)] placeholder:text-gray-400 h-11 rounded-xl" data-testid="gate-email-input" />
          </div>
          <div className="relative">
            <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="pl-10 bg-[var(--ap-canvas)] border-[var(--ap-line)] text-[var(--ap-ink)] placeholder:text-gray-400 h-11 rounded-xl" data-testid="gate-password-input" />
          </div>
          <button type="submit" disabled={loading}
            className="ap-btn ap-btn-fire ap-btn-md w-full disabled:opacity-50"
            data-testid="gate-submit-btn">
            {loading ? "Processing..." : (isLogin ? "SIGN IN & CONTINUE" : "CREATE ACCOUNT & CONTINUE")}
          </button>
        </form>
        <div className="text-center mt-4">
          <button onClick={() => setIsLogin(!isLogin)}
            className="text-[var(--ap-muted)] text-xs hover:text-[var(--ap-violet)] transition-colors bg-transparent border-0 cursor-pointer"
            data-testid="gate-toggle-btn">
            {isLogin ? "New here? Create account" : "Already have an account? Sign in"}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ResultsPage() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [atsParsed, setAtsParsed] = useState(null); // {has_original_file, presence, ...}
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getHeaders, getAuthHeader, user, setUser } = useAuth();
  // Localized pricing (geo-detected). Never render a currency until `ready` —
  // a cached/fallback market can be the wrong country and flashes ₹ before $.
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const p = (amt) => (mktReady ? fmtPrice(mkt, amt) : "…");
  // The ₹99/$14 unlock is charged in the market STAMPED ON THE ANALYSIS (the
  // rules it was scored under) — price the CTA from that market so the number on
  // the button is exactly what Stripe charges, even if the visitor's current
  // geo differs (e.g. scored under US, now browsing from the UK).
  const { cfg: chargeCfg, ready: chargeReady } = usePricingForMarket(analysis?.market, mkt, mktReady);
  const pCharge = (amt) => (chargeReady ? fmtPrice(chargeCfg, amt) : "…");
  // Reveal the page as ONE unit: report + resume pane together (the pane's PDF
  // blob takes ~1-2s and used to pop in late). Fail-open after a short timeout
  // so a slow/failed pane never blocks the report.
  const [atsSettled, setAtsSettled] = useState(false);
  const [paneLoaded, setPaneLoaded] = useState(false);
  const [revealForced, setRevealForced] = useState(false);
  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => setRevealForced(true), 3500);
    return () => clearTimeout(t);
  }, [loading]);
  const pageReady = !loading && (revealForced || (atsSettled && (!atsParsed?.has_original_file || paneLoaded)));
  // Loader-exit transition (same pattern as the Resume Vault): when the loader
  // period ends — the analysis fetch AND the unified-reveal hold — the content
  // mounts underneath while the loader overlay fades out for 300ms, then
  // unmounts. No hard cut.
  const showLoader = loading || (!!analysis && !pageReady);
  const [loaderPhase, setLoaderPhase] = useState("in");
  useEffect(() => {
    if (showLoader) { setLoaderPhase("in"); return undefined; }
    setLoaderPhase("leaving");
    const t = setTimeout(() => setLoaderPhase("done"), 320);
    return () => clearTimeout(t);
  }, [showLoader]);

  const fetchAnalysis = async () => {
    try {
      // Check if specific analysis_id is provided in URL
      const analysisId = searchParams.get("analysis_id");

      // Previously-paid users with no specific analysis requested should land
      // in their Resume Vault (/templates), not on the ₹99 paywall. Avoids
      // the bug where a fresh dashboard analysis pushes the paid one out of
      // the "latest" slot and re-triggers the paywall.
      const alreadyPaid =
        user?.paid_customer === true ||
        user?.has_season_pass === true ||
        user?.has_paid_analysis === true;
      if (!analysisId && alreadyPaid) {
        navigate("/templates", { replace: true });
        return;
      }

      let res;
      if (analysisId) {
        // Fetch specific analysis by ID
        res = await axios.get(`${API}/analysis/${analysisId}`, { headers: getHeaders() });
      } else {
        // Fetch latest analysis for session/user
        res = await axios.get(`${API}/analysis/latest`, { headers: getHeaders() });
      }

      setAnalysis(res.data);
      // Fetch jobs for the target role
      if (res.data.target_role) {
        fetchJobs(res.data.target_role);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error("Failed to load analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async (targetRole) => {
    setJobsLoading(true);
    try {
      const res = await axios.get(`${API}/jobs`, {
        params: { keywords: targetRole, page_size: 5 },
        headers: getHeaders()
      });
      setJobs(res.data.jobs || []);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [user, searchParams]); // eslint-disable-line

  // Parse the resume once (sections/presence + whether we kept the original
  // file). Drives both the "what the ATS reads" card and the layout: we only
  // show the resume pane when their actual uploaded file exists.
  useEffect(() => {
    // IMPORTANT ordering: on first mount this effect fires while analysis is
    // still null — it must NOT mark the check "settled" then, or the report
    // paints alone for a frame (flash), un-readies when the parsed data lands
    // (loader), then reveals. Only settle for a REAL analysis.
    if (!analysis) return undefined;
    const rid = analysis.resume_id;
    if (!rid) { setAtsParsed(null); setAtsSettled(true); return undefined; }
    let cancelled = false;
    setAtsSettled(false);   // new document — hold the unified reveal
    setPaneLoaded(false);
    axios.get(`${API}/resume/${rid}/parsed`, { headers: getHeaders() })
      .then((r) => { if (!cancelled) setAtsParsed(r.data); })
      .catch(() => { if (!cancelled) setAtsParsed(null); })
      .finally(() => { if (!cancelled) setAtsSettled(true); });
    return () => { cancelled = true; };
  }, [analysis?.resume_id]); // eslint-disable-line

  // Auto-trigger payment if returning from auth
  useEffect(() => {
    if (user && searchParams.get("action") === "pay" && analysis && analysis.payment_status !== "paid") {
      handlePayment();
    }
  }, [user, analysis]); // eslint-disable-line

  // Admin-granted free analysis: one full AI unlock (report + rewrite), no payment.
  // Runs a 3-step LLM pipeline server-side — takes up to a minute.
  const claimFreeAnalysis = async () => {
    if (unlocking) return; // double-click guard during the long pipeline
    setUnlocking(true);
    toast.info("Generating your full AI analysis — this takes about a minute…", { duration: 60000 });
    try {
      const res = await axios.post(`${API}/analysis/${analysis.id}/claim-free`, {},
        { headers: getHeaders(), timeout: 180000 });
      setAnalysis(res.data);
      setUser((prev) => prev ? { ...prev, has_free_analysis: false } : prev); // grant consumed
      toast.dismiss();
      toast.success("Free analysis claimed — opening your optimized resume!");
      // The deliverable is the rewritten resume — take the user to it.
      setTimeout(() => navigate("/templates"), 1200);
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "Could not claim the free analysis — please retry");
    } finally {
      setUnlocking(false);
    }
  };

  const handlePaymentClick = () => {
    if (!user) {
      setShowAuthGate(true);
      // The auth card renders far below the unlock buttons — bring it to the user.
      setTimeout(() => {
        document.querySelector('[data-testid="auth-gate-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }
    // A granted free unlock takes priority over asking for money.
    if (user.has_free_analysis) {
      claimFreeAnalysis();
      return;
    }
    handlePayment();
  };

  const handleAuthSuccess = () => {
    setShowAuthGate(false);
    // After auth, proceed to payment
    setTimeout(() => handlePayment(), 500);
  };

  // Score band drives the page's tone: celebrate + next steps at the top,
  // fix-it funnel at the bottom. Keeps the page coherent with an honest score.
  const band = analysis ? (analysis.score >= 85 ? "high" : analysis.score >= 55 ? "mid" : "low") : "low";

  const handleShare = async () => {
    const text = `I scored ${analysis.score}/100 on the PlacementOS ATS resume check 🚀 Think your resume can beat mine?`;
    const url = "https://placementos.ai";
    try {
      if (navigator.share) { await navigator.share({ title: "My ATS Score", text, url }); return; }
    } catch { return; /* user dismissed the share sheet */ }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Score copied — paste it anywhere!");
    } catch { /* clipboard unavailable */ }
  };

  const handlePayment = async () => {
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payment/create-checkout`, {
        analysis_id: analysis.id,
        origin_url: originUrl,
        ...getMetaAttributionPayload(),
      }, { headers: { ...getHeaders(), "Content-Type": "application/json" } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed to initiate");
    }
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await axios.post(`${API}/analysis/${analysis.id}/unlock`, {}, { headers: getHeaders() });
      setAnalysis(res.data);
      toast.success("Report unlocked! Real AI analysis complete.");
    } catch (err) {
      if (err.response?.status === 402) {
        handlePaymentClick();
      } else {
        toast.error("Failed to unlock report");
      }
    } finally {
      setUnlocking(false);
    }
  };

  const downloadResume = () => {
    if (!analysis?.rewritten_resume?.improved_resume) return;
    const blob = new Blob([analysis.rewritten_resume.improved_resume], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ats_optimized_resume.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Fixed sibling overlay at z-40: below the global navbar (z-50) so the
  // chrome never blinks, and outside the content tree so card entrance
  // transforms (.ap-rise) can't hijack its positioning or opacity. ap-root
  // supplies the canvas background. Label follows the loader period we're in:
  // fetching the analysis, then holding for the unified reveal (report +
  // resume pane show together instead of the pane popping in late).
  const loaderOverlay = (
    <div className={`ap-root fixed inset-0 z-40 ${loaderPhase === "leaving" ? "ap-loader-leave" : ""}`}>
      <POSLoader
        fullScreen
        label={loading ? "Reading your resume…" : "Preparing your report…"}
        sub={loading ? "Scoring against recruiter dimensions." : "Laying out your resume next to the diagnosis."}
      />
    </div>
  );
  // Every content branch below returns through this so the fading loader
  // overlays whichever screen replaces it (report, login teaser, empty state).
  const withLoaderExit = (node) => (
    <>
      {loaderPhase !== "done" && loaderOverlay}
      {node}
    </>
  );

  if (showLoader && loaderPhase === "in") {
    return loaderOverlay;
  }

  if (!analysis) {
    return withLoaderExit(
      <div className="ap-root ap-mesh min-h-screen pt-24 flex items-center justify-center px-4" data-testid="no-analysis">
        <Card className="ap-card ap-pop ap-rise max-w-md w-full relative overflow-hidden border-0">
          <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-4 shadow-[0_10px_24px_-12px_rgba(234,179,8,.45)]" style={{ background: "#FFFBEB" }}>
              <AlertTriangle className="w-8 h-8 text-[#eab308]" />
            </div>
            <span className="ap-eyebrow">Nothing to show yet</span>
            <p className="ap-display text-[var(--ap-ink)] text-xl font-extrabold mb-2 mt-2">No Analysis Found</p>
            <p className="text-[var(--ap-muted)] text-sm mb-6 max-w-xs mx-auto">Upload your resume and complete the interview first.</p>
            <button onClick={() => navigate("/intake")} className="ap-btn ap-btn-fire ap-btn-md" data-testid="start-analysis-btn">
              Start New Analysis <ArrowRight className="w-4 h-4" />
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Belt-and-suspenders: any user flagged paid_customer (admin-marked-paid),
  // a season-pass holder, or anyone with a previously-paid analysis bypasses
  // the paywall — even if the *current* analysis row is still pending.
  // Unlocking is PER-ANALYSIS: only an analysis whose paid/free-grant pipeline
  // actually ran has a report + rewrite to show. A user-level "paid before"
  // flag must not render UNLOCKED sections on a score-only analysis (empty
  // report, dead download button). Season Pass is the all-inclusive exception.
  const analysisUnlocked =
    ["paid", "free_grant"].includes(analysis.payment_status) && analysis.status === "complete";
  const isPaid = analysisUnlocked || user?.has_season_pass === true;
  const improvedScore = Number(analysis.rewritten_resume?.estimated_new_score) || 0;
  const improvedCategory = improvedScore <= 40 ? "Failed" : improvedScore <= 70 ? "Moderate" : "Passed";
  const showBeforeAfter = analysisUnlocked && improvedScore > analysis.score;

  // --- Profile links present in the resume (extracted embedded links + plain
  //     text). We never fabricate links, so we surface what the candidate has
  //     and guide them to add the rest. ---
  const _links = analysis.links || [];
  const _linkHay = ((analysis.resume_text || "") + " " + _links.join(" ")).toLowerCase();
  const hasLinkedIn = _linkHay.includes("linkedin.com");
  const hasGitHub = _linkHay.includes("github.com");
  // Portfolio = a REAL http(s) URL that isn't LinkedIn/GitHub. Requiring the
  // scheme avoids false positives from the word "portfolio" or an email domain
  // (e.g. ...@outlook.com must NOT count as a website).
  const hasPortfolio = (_linkHay.match(/https?:\/\/[^\s)]+/gi) || [])
    .some((s) => { const x = s.toLowerCase(); return !x.includes("linkedin.com") && !x.includes("github.com"); });
  const roleIsTechnical = /(develop|engineer|programmer|software|\bdata\b|devops|scientist|\bml\b|\bai\b|back.?end|front.?end|full.?stack|\bsde\b)/i.test(analysis.target_role || "");

  // --- Target-fit mismatch: the optimize barely moved the score because the
  //     resume doesn't match the target role (a CONTENT gap — keyword match /
  //     quantified results — not a wording gap the rewrite can fix). Only after
  //     they've optimized, so it explains a small/zero before->after gain. ---
  const _kwFlaw = (analysis.flaws || []).some((f) =>
    /keyword\s*match|match for your target|low keyword/i.test((((f || {}).title || "") + " " + ((f || {}).description || ""))));
  const targetMismatch = analysisUnlocked && improvedScore > 0 && _kwFlaw &&
    ((improvedScore - analysis.score) < 8 || improvedScore < 70);

  // Freemium gate (Resume Worded / Enhancv model): the DIAGNOSIS is free —
  // honest score, which checks failed, severity. The PRESCRIPTION is paid —
  // fix instructions, keyword gaps, per-dimension details blur until THIS
  // analysis is unlocked. 85+ scorers stay fully open (Season Pass upsell).
  const lockedDetails = !isPaid && band !== "high";

  // Keep the banner truthful: only when THIS analysis is unlocked.
  const showUnlockedBanner = analysisUnlocked;

  // Score-reveal gate: anonymous users upload + analyze freely, but a free
  // account is required to SEE the score. Captures every engaged user into
  // the nurture funnel at peak curiosity; login links the session's analysis
  // to the account automatically (/session/link in AuthContext).
  if (!user) {
    return withLoaderExit(
      <div className="ap-root min-h-screen pt-24 pb-16" data-testid="results-page">
        <div className="ap-section" style={{ maxWidth: "768px" }}>
          <div className="flex flex-col gap-6">
            <Card className="ap-card ap-rise overflow-hidden border-0" data-testid="score-reveal-teaser">
              <div className="ap-mesh">
                <CardContent className="pt-10 pb-8 text-center relative z-10">
                  <span className="ap-eyebrow">Your analysis is ready</span>
                  <div className="mt-5">
                    <ScoreGauge score={analysis.score} category={analysis.category} masked />
                  </div>
                  <p className="ap-display text-[var(--ap-ink)] text-2xl font-extrabold mt-6">
                    Your ATS score is <span className="ap-grad-text">locked &amp; ready</span>
                  </p>
                  <p className="text-[var(--ap-muted)] text-sm mt-2.5 max-w-md mx-auto leading-relaxed">
                    We checked your resume against {analysis.dimensions?.length || 10} recruiter dimensions
                    {analysis.flaws?.length > 0 ? ` and found ${analysis.flaws.length} things worth your attention` : ""}.
                  </p>
                </CardContent>
              </div>
            </Card>
            <div className="ap-rise ap-rise-1">
              <AuthGate
                onSuccess={() => {}}
                defaultMode="register"
                title="Create your free account to reveal your score"
                subtitle="Takes 30 seconds — your analysis is saved to your account"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return withLoaderExit(
    <div className="ap-root min-h-screen pt-24 pb-16" data-testid="results-page">
      <div className="ap-section" style={atsParsed?.has_original_file ? { maxWidth: "1700px", width: "96%" } : { maxWidth: "880px" }}>
        {/* Split ONLY when we have the user's actual uploaded file: A4 resume
            (sticky) on the left + report on the right. No original file (older
            uploads) → report only, single column — never show a "resume" that
            isn't literally what they uploaded. */}
        <div className={atsParsed?.has_original_file ? "grid grid-cols-1 lg:grid-cols-[minmax(460px,794px)_minmax(340px,1fr)] gap-7 items-start" : "flex flex-col gap-6"}>
          {/* LEFT — the user's exact uploaded resume (only when stored) */}
          {atsParsed?.has_original_file && (
            <div className="lg:sticky lg:top-24">
              <ResumeDocPane resumeId={analysis.resume_id} getHeaders={getHeaders} onReady={() => setPaneLoaded(true)} />
            </div>
          )}
          {/* RIGHT — the full report */}
          <div className="flex flex-col gap-6 min-w-0">
          {/* Paid banner — only for THIS unlocked analysis */}
          {showUnlockedBanner && (
            <div className="ap-rise rounded-2xl px-5 py-4 flex items-center gap-3" style={{ background: "#E7F8EE", border: "1px solid rgba(22,163,74,.25)" }} data-testid="paid-banner">
              <CheckCircle className="w-5 h-5 text-[var(--ap-green)] shrink-0" />
              <span className="text-[var(--ap-green)] text-sm font-semibold">Payment Successful. Full Analysis Unlocked.</span>
            </div>
          )}

          {/* Score Card */}
          <Card className="ap-card ap-rise overflow-hidden border-0" data-testid="score-card">
            <div className="ap-mesh">
              <CardContent className="pt-10 pb-8 relative z-10">
                <div className="text-center mb-1">
                  <span className="ap-eyebrow">{band === "high" ? "Recruiter-ready resume" : "Your honest ATS score"}</span>
                </div>
                {showBeforeAfter ? (
                  /* Unlocked + optimized: show the journey, not just the start */
                  <div className="flex items-center justify-center gap-1 sm:gap-4 flex-wrap" data-testid="before-after-gauges">
                    <div className="text-center scale-[0.78] -m-4 opacity-75">
                      <ScoreGauge score={analysis.score} category={analysis.category} />
                      <p className="text-[var(--ap-muted)] text-xs uppercase tracking-wider mt-1">Before</p>
                    </div>
                    <div className="text-center px-1">
                      <div className="inline-flex items-center gap-1.5 font-bold text-sm px-3 py-1.5 rounded-full" style={{ background: "#E7F8EE", color: "var(--ap-green)", border: "1px solid rgba(22,163,74,.3)" }}>
                        <TrendingUp className="w-4 h-4" /> +{improvedScore - analysis.score} pts
                      </div>
                      {analysis.rewritten_resume?.score_verified && (
                        <p className="text-[var(--ap-green)] text-[10px] mt-1.5" title="Re-uploading the optimized resume gives this exact score">✓ engine-verified</p>
                      )}
                      <ArrowRight className="w-6 h-6 text-[var(--ap-green)] mx-auto mt-1 hidden sm:block" />
                    </div>
                    <div className="text-center scale-[0.78] -m-4">
                      <ScoreGauge score={improvedScore} category={improvedCategory} />
                      <p className="text-[var(--ap-green)] text-xs uppercase tracking-wider mt-1 font-semibold">After optimization</p>
                    </div>
                  </div>
                ) : (
                  <ScoreGauge score={analysis.score} category={analysis.category} />
                )}
                <p className="text-center text-[var(--ap-muted)] mt-5 text-sm max-w-md mx-auto leading-relaxed">{analysis.summary}</p>
                {band === "high" && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={handleShare}
                      className="inline-flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-full transition-all cursor-pointer"
                      style={{ background: "#E7F8EE", color: "var(--ap-green)", border: "1px solid rgba(22,163,74,.3)" }}
                      data-testid="share-score-btn"
                    >
                      <Share2 className="w-4 h-4" /> Share my score
                    </button>
                  </div>
                )}
              </CardContent>
            </div>
          </Card>

          {/* Target-fit mismatch — explains a small/zero optimize gain honestly:
              the resume doesn't match the target role (content gap, not wording). */}
          {targetMismatch && (
            <Card className="ap-card ap-rise border-0" data-testid="target-mismatch">
              <CardContent className="py-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: "#FFFBEB" }}>
                  <AlertTriangle className="w-5 h-5 text-[#eab308]" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--ap-ink)] text-sm mb-1">Your resume doesn't strongly match &ldquo;{analysis.target_role || "this role"}&rdquo; yet</p>
                  <p className="text-[var(--ap-muted)] text-sm leading-relaxed">
                    The optimizer polished your wording, but the biggest gaps — keyword match and quantified results for this role — come from <span className="font-medium text-[var(--ap-ink-2)]">real experience</span>, not phrasing, so the score can only move so far. We won't invent skills or metrics you don't have. To go higher: target a role closer to your background, or add genuine projects/skills for {analysis.target_role || "this role"}.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* What the ATS reads (sections + gaps) — always in the report; it's the
              parse, not a claim to be their resume. */}
          <AtsReadCard parsed={atsParsed} />

          {/* Profile links — what the candidate has vs. what a strong resume
              needs (recommended/optional). We never fabricate links, so we guide
              them to add their own in the editor. */}
          <Card className="ap-card ap-rise ap-rise-1 border-0" data-testid="links-card">
            <CardContent className="py-5">
              <span className="ap-eyebrow block mb-3">Your profile links</span>
              <div className="space-y-2.5">
                {[
                  { key: "LinkedIn", has: hasLinkedIn, tag: "Recommended", tagColor: "#2832C2" },
                  { key: "GitHub", has: hasGitHub, tag: roleIsTechnical ? "Recommended" : "Optional", tagColor: roleIsTechnical ? "#2832C2" : "#6b7280" },
                  { key: "Portfolio / website", has: hasPortfolio, tag: "Optional", tagColor: "#6b7280" },
                ].map((l) => (
                  <div key={l.key} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {l.has
                        ? <CheckCircle className="w-4 h-4 text-[var(--ap-green)] shrink-0" />
                        : <span className="w-4 h-4 rounded-full border-2 border-[var(--ap-line)] shrink-0" />}
                      <span className={`text-sm truncate ${l.has ? "text-[var(--ap-ink)]" : "text-[var(--ap-muted)]"}`}>{l.key}</span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0" style={{ background: `${l.tagColor}18`, color: l.tagColor }}>{l.tag}</span>
                    </div>
                    <span className="text-xs font-medium shrink-0" style={{ color: l.has ? "var(--ap-green)" : "var(--ap-orange)" }}>
                      {l.has ? "Added" : "Add it"}
                    </span>
                  </div>
                ))}
              </div>
              {(!hasLinkedIn || (roleIsTechnical && !hasGitHub)) && (
                <button
                  onClick={() => navigate(`/templates?analysis_id=${analysis.id}&edit=1`)}
                  className="ap-btn ap-btn-pri ap-btn-md mt-4 w-full"
                  data-testid="add-links-cta"
                >
                  Add my links in the editor <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </CardContent>
          </Card>

          {/* Journey strip — resume is done; show what's next */}
          {band === "high" && (
            <Card className="ap-card ap-rise ap-rise-1" data-testid="journey-card">
              <CardContent className="py-5">
                <span className="ap-eyebrow block text-center mb-4">Your next moves</span>
                <div className="flex items-center justify-between gap-1 sm:gap-3">
                  <div className="flex-1 text-center">
                    <div className="w-11 h-11 mx-auto rounded-2xl grid place-items-center mb-1.5" style={{ background: "#E7F8EE" }}>
                      <CheckCircle className="w-5 h-5 text-[var(--ap-green)]" />
                    </div>
                    <p className="text-[var(--ap-ink)] text-sm font-semibold">Resume</p>
                    <p className="text-[var(--ap-green)] text-xs font-medium">Recruiter-ready</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                  <button onClick={() => navigate("/skills")} className="flex-1 text-center bg-transparent border-0 cursor-pointer group" data-testid="journey-skills">
                    <div className="w-11 h-11 mx-auto rounded-2xl grid place-items-center mb-1.5 transition-colors" style={{ background: "var(--ap-violet-soft)" }}>
                      <GraduationCap className="w-5 h-5 text-[var(--ap-violet)]" />
                    </div>
                    <p className="text-[var(--ap-ink)] text-sm font-semibold">Skills</p>
                    <p className="text-[var(--ap-muted)] text-xs">{analysis.buzzwords?.missing?.length > 0 ? `${analysis.buzzwords.missing.length} boosters to add` : "Level up next"}</p>
                  </button>
                  <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                  <button onClick={() => navigate("/jobs")} className="flex-1 text-center bg-transparent border-0 cursor-pointer group" data-testid="journey-jobs">
                    <div className="w-11 h-11 mx-auto rounded-2xl grid place-items-center mb-1.5 transition-colors" style={{ background: "var(--ap-violet-soft)" }}>
                      <Briefcase className="w-5 h-5 text-[var(--ap-violet)]" />
                    </div>
                    <p className="text-[var(--ap-ink)] text-sm font-semibold">Jobs</p>
                    <p className="text-[var(--ap-muted)] text-xs">Matches waiting</p>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* What's Working — strengths the engine found */}
          {analysis.strengths?.length > 0 && (
            <Card className="ap-card ap-rise ap-rise-1" data-testid="strengths-card">
              <CardHeader>
                <span className="ap-eyebrow">Free diagnosis</span>
                <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold flex items-center gap-2 mt-1">
                  <Trophy className="w-5 h-5 text-[var(--ap-green)]" /> What's Working
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {analysis.strengths.map((s, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 rounded-xl" style={{ background: "var(--ap-canvas)" }} data-testid={`strength-item-${i}`}>
                    <CheckCircle className="w-4 h-4 text-[var(--ap-green)] shrink-0 mt-0.5" />
                    <p className="text-[var(--ap-ink-2)] text-sm">{s}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Score Breakdown (per-dimension, honest & free) */}
          {analysis.dimensions?.length > 0 && (
            <Card className="ap-card ap-rise ap-rise-2" data-testid="breakdown-card">
              <CardHeader>
                <span className="ap-eyebrow">Dimension by dimension</span>
                <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold mt-1">
                  Score Breakdown{analysis.level ? ` · ${analysis.level.charAt(0).toUpperCase() + analysis.level.slice(1)} level` : ""}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {analysis.dimensions.map((d) => {
                  const ratio = d.max ? d.earned / d.max : 0;
                  const color = ratio >= 0.7 ? "#16a34a" : ratio >= 0.4 ? "#eab308" : "#ef4444";
                  return (
                    <div key={d.key} data-testid={`dim-${d.key}`}>
                      <div className="flex justify-between items-baseline">
                        <span className="text-[var(--ap-ink)] text-sm font-medium">{d.label}</span>
                        <span className="ap-display text-xs font-bold text-[var(--ap-muted)]">{d.earned}/{d.max}</span>
                      </div>
                      <div className="h-2.5 rounded-full overflow-hidden mt-1.5" style={{ background: "var(--ap-canvas-2)" }}>
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.round(ratio * 100)}%`, background: color }} />
                      </div>
                      {d.detail ? (
                        <p className="text-[var(--ap-muted)] text-xs mt-1.5">{d.detail}</p>
                      ) : d.locked ? (
                        /* Server withholds the insight — placeholder bar, no real text */
                        <div className="flex items-center gap-1.5 mt-1.5 cursor-pointer group" onClick={handlePaymentClick} data-testid={`dim-locked-${d.key}`}>
                          <Lock className="w-3 h-3 text-gray-300 group-hover:text-[var(--ap-violet)]" />
                          <div className="h-2 rounded-full w-5/12 group-hover:opacity-80" style={{ background: "var(--ap-canvas-2)" }} />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Flaws — only when there are any; tone adapts to the score band */}
          {analysis.flaws?.length > 0 && (
          <Card className="ap-card ap-rise ap-rise-3" data-testid="flaws-card">
            <CardHeader>
              <span className="ap-eyebrow">{band === "high" ? "Optional polish" : "What's holding you back"}</span>
              <div className="flex items-center justify-between gap-2 flex-wrap mt-1">
                <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${band === "high" ? "text-[#eab308]" : "text-[#ef4444]"}`} />
                  {band === "high" ? "Polish Suggestions" : `Critical Flaws (${analysis.flaws.length})`}
                </CardTitle>
                {lockedDetails && (
                  <button
                    onClick={handlePaymentClick}
                    disabled={unlocking}
                    className="ap-btn ap-btn-pri inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 disabled:opacity-60"
                    data-testid="unlock-fixes-btn"
                  >
                    {unlocking ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating your report…</>
                    ) : (
                      <><Lock className="w-3.5 h-3.5" /> {user?.has_free_analysis ? "Use your FREE unlock 🎁" : `Unlock all fixes — ${pCharge(chargeCfg.prices.one_time)}`}</>
                    )}
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.flaws?.map((flaw, i) => (
                <div key={i} className={`p-4 rounded-2xl ${band === "high" ? "bg-amber-50 border border-amber-100" : "bg-red-50 border border-red-100"}`} data-testid={`flaw-item-${i}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <XCircle className={`w-4 h-4 shrink-0 ${band === "high" ? "text-[#eab308]" : "text-[#ef4444]"}`} />
                    <span className="text-[var(--ap-ink)] text-sm font-semibold">{flaw.title}</span>
                    <Badge className={`text-xs border-0 ${flaw.severity === 'critical' ? 'bg-red-500/15 text-red-500' : flaw.severity === 'major' ? 'bg-yellow-500/15 text-yellow-600' : 'bg-blue-500/15 text-blue-500'}`}>
                      {flaw.severity}
                    </Badge>
                  </div>
                  {lockedDetails || flaw.locked ? (
                    /* The fix never leaves the server for unpaid users — these
                       are pure placeholder bars, not blurred real text. */
                    <div className="ml-6 cursor-pointer group" onClick={handlePaymentClick} data-testid={`flaw-locked-${i}`}>
                      <div className="space-y-1.5 mt-1">
                        <div className="h-2.5 rounded-full w-11/12" style={{ background: "var(--ap-canvas-2)" }} />
                        <div className="h-2.5 rounded-full w-7/12" style={{ background: "var(--ap-canvas-2)" }} />
                      </div>
                      <p className="text-[var(--ap-violet)] text-xs font-medium mt-2 inline-flex items-center gap-1 group-hover:underline">
                        <Lock className="w-3 h-3" /> Unlock the fix
                      </p>
                    </div>
                  ) : (
                    <p className="text-[var(--ap-muted)] text-xs ml-6">{flaw.description}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
          )}

          {/* Buzzword Density */}
          <Card className="ap-card ap-rise ap-rise-4" data-testid="buzzwords-card">
            <CardHeader>
              <span className="ap-eyebrow">Recruiter keyword scan</span>
              <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold mt-1">Keyword Match</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="ap-display text-3xl font-extrabold ap-grad-text">{analysis.buzzwords?.density}%</div>
                <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "var(--ap-canvas-2)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min(analysis.buzzwords?.density || 0, 100)}%`, background: "var(--ap-fire)" }}
                  />
                </div>
              </div>
              {analysis.buzzwords?.missing?.length > 0 && (
                <div>
                  <p className="text-[var(--ap-muted)] text-xs mb-2">
                    {band === "high" ? "Boosters — skills that would push your match even higher:" : "Missing Keywords:"}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.buzzwords.missing.map((kw, i) => (
                      <span key={i} className={`px-2.5 py-1 text-xs rounded-full border font-medium ${band === "high" ? "bg-[var(--ap-violet-soft)] text-[var(--ap-violet)] border-transparent" : "bg-red-500/10 text-red-500 border-red-500/20"}`}>{kw}</span>
                    ))}
                    {analysis.buzzwords.missing_locked > 0 && (
                      <>
                        {/* Server withholds locked keywords — masked placeholder chips */}
                        {Array.from({ length: Math.min(analysis.buzzwords.missing_locked, 5) }).map((_, i) => (
                          <span key={`l-${i}`} className="px-2.5 py-1 text-xs rounded-full border text-gray-300 tracking-widest select-none" style={{ background: "var(--ap-canvas-2)", borderColor: "var(--ap-line)" }}>•••••</span>
                        ))}
                        <button
                          onClick={handlePaymentClick}
                          className="px-2.5 py-1 text-xs rounded-full border bg-[var(--ap-violet-soft)] text-[var(--ap-violet)] border-[var(--ap-violet)]/30 font-semibold cursor-pointer inline-flex items-center gap-1"
                          data-testid="unlock-keywords-btn"
                        >
                          <Lock className="w-3 h-3" /> +{analysis.buzzwords.missing_locked} more
                        </button>
                      </>
                    )}
                  </div>
                  {band === "high" && (
                    <button
                      onClick={() => navigate("/skills")}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ap-violet)] hover:underline bg-transparent border-0 cursor-pointer"
                      data-testid="skills-bridge-btn"
                    >
                      <GraduationCap className="w-4 h-4" /> Build these skills in your Skill Path <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Listings Section */}
          <Card className="ap-card ap-rise ap-rise-4" data-testid="job-listings-card">
            <CardHeader>
              <span className="ap-eyebrow">Opportunities waiting</span>
              <div className="flex items-center gap-2 mt-1">
                <Briefcase className="w-5 h-5 text-[var(--ap-violet)]" />
                <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold">
                  Matching Jobs for {analysis.target_role || "You"}
                </CardTitle>
              </div>
              <CardDescription className="text-[var(--ap-muted)]">Live opportunities from top companies</CardDescription>
            </CardHeader>
            <CardContent>
              {jobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--ap-violet)" }} />
                </div>
              ) : jobs.length > 0 ? (
                <div className="space-y-3">
                  {jobs.slice(0, 4).map((job) => {
                    const jobUrl = safeExternalUrl(job.url);
                    const attributionUrl = safeExternalUrl(job.attribution_url);
                    const stableKey = job.job_id || job.url || `${job.provider || "job"}:${job.title}:${job.company}`;
                    return (
                      <div key={stableKey} className="ap-pop p-4 rounded-2xl" style={{ border: "1px solid var(--ap-line)", background: "#fff" }}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-[var(--ap-ink)] font-semibold text-sm mb-1 line-clamp-1">{job.title}</h4>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--ap-muted)] mb-2">
                              <div className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                <span className="line-clamp-1">{job.company}</span>
                              </div>
                              {job.locations && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  <span className="line-clamp-1">{job.locations}</span>
                                </div>
                              )}
                              {job.attribution_text && (
                                attributionUrl ? (
                                  <a
                                    href={attributionUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline underline-offset-2 hover:text-[var(--ap-violet)]"
                                    data-testid="results-job-attribution"
                                  >
                                    {job.attribution_text}
                                  </a>
                                ) : (
                                  <span data-testid="results-job-attribution">{job.attribution_text}</span>
                                )
                              )}
                            </div>
                            {job.description && (
                              <p className="text-xs text-[var(--ap-muted)] line-clamp-2">{job.description}</p>
                            )}
                          </div>
                          {jobUrl ? (
                            <a
                              href={jobUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ap-btn ap-btn-ghost no-underline shrink-0"
                              style={{ fontSize: 12, padding: "8px 12px" }}
                              data-testid="results-view-job"
                            >
                              View job <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                          ) : (
                            <span className="text-xs text-[var(--ap-muted)] shrink-0">Link unavailable</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-[var(--ap-muted)] text-sm py-8">No jobs found at the moment</p>
              )}
            </CardContent>
          </Card>

          {/* High scorers: the resume is done — sell the next step, not a fix */}
          {!isPaid && band === "high" && (
            <Card className="ap-card ap-rise border-0 rounded-[22px] text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #16a34a, #15803d)" }} data-testid="career-os-card">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
              <CardContent className="py-8 relative z-10">
                <div className="text-center max-w-2xl mx-auto">
                  <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm font-semibold">Resume: Done ✓</span>
                  </div>
                  <h3 className="ap-display text-2xl md:text-3xl font-extrabold mb-3">
                    Your resume is ready. Now put it to work.
                  </h3>
                  <p className="text-white/90 text-base mb-6 leading-relaxed">
                    A great resume gets you through the filter — <strong>skills and targeted applications get you hired</strong>. The Full Career OS gives you matched jobs, a personalized skill path{analysis.buzzwords?.missing?.length > 0 ? ` (starting with ${analysis.buzzwords.missing.slice(0, 2).join(" & ")})` : ""}, and your career companion for a full year.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button
                      onClick={() => navigate("/season-pass")}
                      className="bg-white text-[#15803d] hover:bg-gray-100 font-bold px-8 py-4 rounded-xl text-base transition-all flex items-center gap-2 shadow-lg cursor-pointer border-0"
                      data-testid="career-os-cta-btn"
                    >
                      <Zap className="w-5 h-5" /> Unlock Full Career OS
                    </button>
                    <button
                      onClick={() => navigate("/jobs")}
                      className="text-white/90 hover:text-white text-sm underline bg-transparent border-0 cursor-pointer"
                    >
                      or browse your job matches first →
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* FOMO Section — only for unpaid analyses below the high band */}
          {!isPaid && band !== "high" && <Card className="ap-card ap-rise border-0 rounded-[22px] text-white relative overflow-hidden" style={{ background: "var(--ap-fire)" }} data-testid="fomo-card">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
            <CardContent className="py-8 relative z-10">
              <div className="text-center max-w-2xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-semibold">Limited Time Opportunity</span>
                </div>
                <h3 className="ap-display text-2xl md:text-3xl font-extrabold mb-3">
                  You're Eligible for 450+ Job Openings!
                </h3>
                <p className="text-white/90 text-base mb-6 leading-relaxed">
                  Companies are actively searching for professionals like you. But here's the catch - <strong>your resume needs to stand out</strong> in their ATS systems. Right now, you're missing out on opportunities because your resume isn't optimized.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                    <div className="ap-display text-3xl font-extrabold mb-1">450+</div>
                    <div className="text-sm text-white/80">Active Job Openings</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                    <div className="ap-display text-3xl font-extrabold mb-1">3.2x</div>
                    <div className="text-sm text-white/80">Higher Response Rate</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl">
                    <div className="ap-display text-3xl font-extrabold mb-1">{pCharge(chargeCfg.prices.one_time)}</div>
                    <div className="text-sm text-white/80">One-Time Investment</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={handlePaymentClick}
                    className="bg-white text-[var(--ap-violet)] hover:bg-gray-100 font-bold px-8 py-4 rounded-xl text-base transition-all flex items-center gap-2 shadow-lg cursor-pointer border-0"
                    data-testid="fomo-cta-btn"
                  >
                    <Zap className="w-5 h-5" />
                    Optimize My Resume for {pCharge(chargeCfg.prices.one_time)}
                  </button>
                  <p className="text-white/70 text-xs">
                    Join 15,247+ professionals who got hired faster
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>}

          {/* Auth Gate / Paywall / Full Report */}
          {showAuthGate && !user ? (
            <AuthGate onSuccess={handleAuthSuccess} />
          ) : !analysisUnlocked && user?.has_season_pass ? (
            /* Season Pass holder viewing a score-only analysis: no ₹99 ask —
               their full pipeline runs from the dashboard. */
            <Card className="ap-card ap-rise" data-testid="seasonpass-hint-card">
              <CardContent className="py-7 text-center">
                <span className="ap-eyebrow">Career OS member</span>
                <p className="ap-display text-[var(--ap-ink)] text-sm font-semibold mb-3 mt-2 max-w-md mx-auto leading-relaxed">This analysis has the free score only. As a Career OS member, run the full AI analysis from your dashboard — no extra payment.</p>
                <button onClick={() => navigate("/dashboard")} className="ap-btn ap-btn-pri ap-btn-md" data-testid="seasonpass-dashboard-btn">
                  Open Dashboard <ArrowRight className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          ) : !analysisUnlocked ? (
            <Card className="ap-card ap-rise relative overflow-hidden border-0" data-testid="paywall-card">
              <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: "var(--ap-fire)" }} />
              <CardContent className="pt-8 pb-10 px-6">
                <div className="relative mb-6">
                  <div className="locked-blur p-4 rounded-2xl space-y-2" style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}>
                    {band === "high" ? (
                      <>
                        <p className="text-[var(--ap-ink)] text-sm">Detailed Report: A deep-dive across all 10 scoring dimensions with prioritized, role-specific recommendations...</p>
                        <p className="text-[var(--ap-ink)] text-sm">AI-Polished Resume: A professionally refined rewrite that keeps your strong base and sharpens every bullet...</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[var(--ap-ink)] text-sm">Detailed Report: Your resume needs significant improvements in key areas including keyword optimization, formatting, and quantified achievements...</p>
                        <p className="text-[var(--ap-ink)] text-sm">AI-Rewritten Resume: We've optimized your resume with targeted changes for maximum ATS compatibility...</p>
                      </>
                    )}
                  </div>
                  {/* purely decorative fade so the blurred preview reads as "more below" */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-2xl" style={{ background: "linear-gradient(to bottom, transparent, var(--ap-card))" }} />
                </div>

                <div className="text-center">
                  <span className="ap-eyebrow">{band === "high" ? "Go deeper" : "Unlock the fixes"}</span>
                  <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto my-3 shadow-[0_12px_28px_-12px_rgba(91,61,245,.5)]" style={{ background: "var(--ap-violet-soft)" }}>
                    <Lock className="w-7 h-7 text-[var(--ap-violet)]" />
                  </div>
                  <h3 className="ap-display text-[var(--ap-ink)] text-xl md:text-2xl font-extrabold mb-2">
                    {band === "high" ? "Optional: Expert AI Deep-Dive & Rewrite" : "Unlock Full AI Analysis & ATS-Fixed Resume"}
                  </h3>
                  <p className="text-[var(--ap-muted)] text-sm mb-4 max-w-md mx-auto leading-relaxed">
                    {band === "high"
                      ? "Your score is already strong — get the AI's full report and a polished rewrite as a second pair of eyes."
                      : "Real AI-powered scoring, detailed report, and rewritten resume."}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 mb-5 text-xs font-medium text-[var(--ap-muted)]">
                    <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> Full report</span>
                    <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> AI-rewritten resume</span>
                    <span className="inline-flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> Clean download</span>
                  </div>
                  <button
                    onClick={handlePaymentClick}
                    className="ap-btn ap-btn-fire ap-btn-lg uppercase tracking-wide"
                    data-testid="pay-btn"
                  >
                    {unlocking ? "UNLOCKING..." : user?.has_free_analysis ? "CLAIM FREE FULL ANALYSIS 🎁" : user ? `UNLOCK FOR ${pCharge(chargeCfg.prices.one_time)}` : `SIGN IN & UNLOCK FOR ${pCharge(chargeCfg.prices.one_time)}`}
                  </button>
                  <p className="text-[var(--ap-muted)] text-xs mt-3">One-time payment. Instant delivery.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Full Report — only when the report actually exists */}
              {analysis.report && (
              <Card className="ap-card ap-rise" style={{ borderColor: "rgba(22,163,74,.25)" }} data-testid="full-report-card">
                <CardHeader>
                  <span className="ap-eyebrow" style={{ color: "var(--ap-green)" }}>Your prescription</span>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <CheckCircle className="w-5 h-5 text-[var(--ap-green)]" />
                    <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold">Full Analysis Report</CardTitle>
                    <Badge className="bg-green-500/15 text-green-600 border-0 text-xs">UNLOCKED</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {analysis.report?.executive_summary && (
                    <div className="p-4 rounded-2xl" style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}>
                      <h4 className="text-[var(--ap-ink)] text-sm font-semibold mb-2">Executive Summary</h4>
                      <p className="text-[var(--ap-muted)] text-sm leading-relaxed">{analysis.report.executive_summary}</p>
                    </div>
                  )}

                  {analysis.report?.recommendations?.map((rec, i) => (
                    <div key={i} className="p-4 rounded-2xl bg-blue-50 border border-blue-100" data-testid={`recommendation-${i}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <TrendingUp className="w-4 h-4 text-[var(--ap-violet)]" />
                        <span className="text-[var(--ap-ink)] text-sm font-medium">{rec.title}</span>
                        <Badge className={`text-xs border-0 ${rec.impact === 'high' ? 'bg-red-500/15 text-red-500' : rec.impact === 'medium' ? 'bg-yellow-500/15 text-yellow-600' : 'bg-blue-500/15 text-blue-500'}`}>
                          {rec.impact} impact
                        </Badge>
                      </div>
                      <p className="text-[var(--ap-muted)] text-xs ml-6">{rec.description}</p>
                    </div>
                  ))}

                  {/* One number, the real one: the engine-verified optimized score.
                      The report LLM's own guess (improved_score_estimate) showed a
                      third conflicting figure (45 -> 90 above, "75" here). */}
                  {(improvedScore > 0 || analysis.report?.improved_score_estimate) && (
                    <div className="p-4 rounded-2xl" style={{ background: "#E7F8EE", border: "1px solid rgba(22,163,74,.2)" }}>
                      <p className="text-[var(--ap-green)] text-sm font-medium">
                        {improvedScore > 0 ? (
                          <>Verified Score After Optimization: <span className="ap-display text-lg font-extrabold">{improvedScore}/100</span>
                          {analysis.rewritten_resume?.score_verified && <span className="text-xs ml-1.5" title="Re-uploading the optimized resume gives this exact score">✓</span>}</>
                        ) : (
                          <>Estimated Score After Fixes: <span className="ap-display text-lg font-extrabold">{analysis.report.improved_score_estimate}/100</span></>
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* Rewritten Resume — only when the rewrite actually exists */}
              {analysis.rewritten_resume?.improved_resume && (
              <Card className="ap-card ap-rise" data-testid="rewritten-resume-card">
                <CardHeader>
                  <span className="ap-eyebrow">Ready to download</span>
                  <CardTitle className="ap-display text-[var(--ap-ink)] text-lg font-extrabold flex items-center gap-2 mt-1">
                    <FileText className="w-5 h-5 text-[var(--ap-violet)]" /> ATS-Optimized Resume
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analysis.rewritten_resume?.changes_made && (
                    <div className="mb-4">
                      <p className="text-[var(--ap-muted)] text-xs mb-2 font-medium">Changes Made:</p>
                      <ul className="space-y-1">
                        {analysis.rewritten_resume.changes_made.map((change, i) => (
                          <li key={i} className="text-[var(--ap-green)] text-xs flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" /> {change}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {analysis.rewritten_resume?.keywords_added?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[var(--ap-muted)] text-xs mb-2 font-medium">Keywords Added:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {analysis.rewritten_resume.keywords_added.map((kw, i) => (
                          <span key={i} className="px-2.5 py-1 text-xs bg-[var(--ap-violet-soft)] text-[var(--ap-violet)] rounded-full border border-transparent font-medium">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="p-4 rounded-2xl max-h-60 overflow-y-auto" style={{ background: "var(--ap-canvas)", border: "1px solid var(--ap-line)" }}>
                    <pre className="text-[var(--ap-ink)] text-xs whitespace-pre-wrap font-mono leading-relaxed">
                      {analysis.rewritten_resume?.improved_resume || "Resume content"}
                    </pre>
                  </div>

                  <button
                    onClick={downloadResume}
                    className="ap-btn ap-btn-pri ap-btn-md w-full mt-4 uppercase tracking-wide"
                    data-testid="download-resume-btn"
                  >
                    <Download className="w-4 h-4" /> DOWNLOAD OPTIMIZED RESUME
                  </button>
                </CardContent>
              </Card>
              )}

              <div className="ap-card ap-rise p-6 text-center space-y-3">
                <span className="ap-eyebrow">What's next</span>
                <p className="ap-display text-[var(--ap-ink)] text-base font-extrabold">Your resume is ready. Now let's get you the job.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                  <button onClick={() => navigate("/templates")} className="ap-btn ap-btn-ghost ap-btn-md" data-testid="goto-templates-btn">
                    <FileText className="w-4 h-4" /> Customize Template
                  </button>
                  <button onClick={() => navigate("/season-pass")} className="ap-btn ap-btn-fire ap-btn-md" data-testid="goto-season-pass-btn">
                    Get Full Access &mdash; {p(mkt.prices.pass_annual)}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* New Analysis */}
          <div className="text-center pb-8">
            <button
              onClick={() => navigate("/intake")}
              className="text-[var(--ap-muted)] text-sm hover:text-[var(--ap-ink)] transition-colors bg-transparent border-0 cursor-pointer"
              data-testid="new-analysis-btn"
            >
              Start a new analysis &rarr;
            </button>
          </div>
          </div>{/* /right column */}
        </div>{/* /split grid */}
      </div>
    </div>
  );
}
